import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { BuiltInDataTypes } from "abreact/data/DataType";
import { AbInlineNotification } from "abreact/feedback/AbInlineNotification";
import { AbFieldGroup } from "abreact/form/AbFieldGroup";
import { AbPasswordField } from "abreact/form/AbPasswordField";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbVBox } from "abreact/layout/AbVBox";
import { AbDialog } from "abreact/popup/AbDialog";
import { Record } from "immutable";
import * as React from "react";
export function AbResetPasswordDialog(props) {
  const commonProps = ReactUtils.componentProps("ab-reset-password-dialog", props);
  // state to store the field values
  const [passwordRecord, setPasswordRecord] = React.useState(new ImmutablePasswordRecord({}));
  // state to store whether to display an inline error
  const [showError, setShowError] = React.useState(false);
  return /*#__PURE__*/React.createElement(AbDialog, _extends({}, commonProps, {
    title: props.title,
    cancelButton: true,
    submitButtonProps: {
      disabled: passwordRecord.password === "" || passwordRecord.confirmPassword === "" || props.askForOldPassword && passwordRecord.oldPassword === ""
    },
    onSubmit: () => onSubmit(props, passwordRecord, setShowError)
  }), /*#__PURE__*/React.createElement(AbVBox, {
    className: "ab-reset-password-dialog-content",
    spacing: true
  }, showError && /*#__PURE__*/React.createElement(AbInlineNotification, {
    severity: "error",
    message: AbReactResources.resetPasswordDialog_passwordsDoNotMatchErrorMessage(),
    className: "ab-flexible"
  }), /*#__PURE__*/React.createElement(AbFieldGroup, null, props.askForOldPassword && /*#__PURE__*/React.createElement(AbPasswordField, {
    className: "ab-reset-password-dialog-old-password-field",
    dataTestId: "reset-password-dialog-old-password-field",
    label: AbReactResources.resetPasswordDialog_oldPasswordFieldLabel(),
    subject: passwordRecord,
    accessor: oldPasswordAccessor,
    currentValue: passwordRecord.oldPassword,
    onChange: (newSubject, newValue) => {
      setPasswordRecord(newSubject.with({
        oldPassword: newValue ?? ""
      }));
    },
    editManager: null
  }), /*#__PURE__*/React.createElement(AbPasswordField, {
    dataTestId: "reset-password-dialog-password-field",
    label: props.askForOldPassword ? AbReactResources.resetPasswordDialog_passwordFieldLabelWithOldPassword() : AbReactResources.resetPasswordDialog_passwordFieldLabel(),
    subject: passwordRecord,
    accessor: passwordAccessor,
    currentValue: passwordRecord.password,
    onChange: (newSubject, newValue) => {
      setPasswordRecord(newSubject.with({
        password: newValue ?? ""
      }));
    },
    editManager: null
  }), /*#__PURE__*/React.createElement(AbPasswordField, {
    dataTestId: "reset-password-dialog-confirm-field",
    label: props.askForOldPassword ? AbReactResources.resetPasswordDialog_confirmPasswordFieldLabelWithOldPassword() : AbReactResources.resetPasswordDialog_confirmPasswordFieldLabel(),
    subject: passwordRecord,
    accessor: confirmPasswordAccessor,
    currentValue: passwordRecord.confirmPassword,
    onChange: (newSubject, newValue) => {
      setPasswordRecord(newSubject.with({
        confirmPassword: newValue ?? ""
      }));
    },
    editManager: null
  }))));
}
async function onSubmit(props, subject, setShowError) {
  if (subject.password !== subject.confirmPassword) {
    setShowError(true);
    return false;
  } else if (props.askForOldPassword && props.onSubmitWithOldPassword) {
    props.onSubmitWithOldPassword(subject.oldPassword, subject.password);
    return true;
  } else {
    props.onSubmit && props.onSubmit(subject.password);
    return true;
  }
}
class ImmutablePasswordRecord extends Record({
  oldPassword: "",
  password: "",
  confirmPassword: ""
}) {
  constructor(values = {}) {
    super(values);
  }
  with(values) {
    return this.merge(values);
  }
}

// NOTE: the hard coded displayName strings are not localization bugs
// because they are overridden by the field label props above
const oldPasswordAccessor = {
  displayName: "Current Password",
  dataType: BuiltInDataTypes.PASSWORD,
  getClientValue: subject => subject.oldPassword || null,
  setClientValue: (subject, newString) => {
    return subject.with({
      oldPassword: newString ?? undefined
    });
  }
};
const passwordAccessor = {
  displayName: "Password",
  dataType: BuiltInDataTypes.PASSWORD,
  getClientValue: subject => subject.password || null,
  setClientValue: (subject, newString) => {
    return subject.with({
      password: newString ?? undefined
    });
  }
};
const confirmPasswordAccessor = {
  displayName: "Confirm Password",
  dataType: BuiltInDataTypes.PASSWORD,
  getClientValue: subject => subject.confirmPassword || null,
  setClientValue: (subject, newString) => {
    return subject.with({
      confirmPassword: newString ?? undefined
    });
  }
};
//# sourceMappingURL=AbResetPasswordDialog.js.map
